import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Crown, Check, Loader2, Sparkles, Mic, Headphones, PenLine } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchSubscriptionStatus();
    
    // If returning from Stripe, poll for status
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/user-status`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info('Verificando pago... Por favor revisa tu email para confirmación.');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/status/${sid}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('¡Pago exitoso! Ya tienes acceso Premium.');
        fetchSubscriptionStatus();
        // Clear the URL params
        navigate('/subscription', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('La sesión de pago ha expirado. Intenta de nuevo.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    
    try {
      const response = await axios.post(`${API}/subscriptions/create-checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Error al procesar el pago. Intenta de nuevo.');
      setProcessingPlan(null);
    }
  };

  const plans = [
    {
      id: 'monthly',
      name: 'Plan Mensual',
      price: '€9.99',
      period: '/mes',
      features: [
        'Acceso a todos los ejercicios',
        'Feedback de IA ilimitado',
        'Audio real en Listening',
        'Voice-to-Text en Speaking',
        'Estadísticas detalladas'
      ],
      popular: false
    },
    {
      id: 'yearly',
      name: 'Plan Anual',
      price: '€95.99',
      period: '/año',
      savings: 'Ahorra €24',
      features: [
        'Todo lo del Plan Mensual',
        '2 meses GRATIS',
        'Ejercicios exclusivos C2',
        'Soporte prioritario',
        'Certificado de progreso'
      ],
      popular: true
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Current Status */}
        {subscriptionStatus?.is_premium && (
          <div className="mb-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-sm animate-fade-in-up">
            <div className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-amber-500" />
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  Eres usuario Premium
                </h2>
                <p className="text-slate-600">
                  Plan: {subscriptionStatus.plan === 'yearly' ? 'Anual' : 'Mensual'} · 
                  Expira: {new Date(subscriptionStatus.expires_at).toLocaleDateString('es-ES')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Premium</span>
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
            Desbloquea todo tu potencial
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Accede a funcionalidades exclusivas y prepárate mejor para tu examen Cambridge
          </p>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white p-6 border border-slate-200 animate-fade-in-up">
            <Headphones className="w-10 h-10 text-purple-500 mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">Audio Real en Listening</h3>
            <p className="text-sm text-slate-600">
              Escucha pronunciación nativa generada con IA de alta calidad
            </p>
          </div>
          <div className="bg-white p-6 border border-slate-200 animate-fade-in-up animation-delay-100">
            <Mic className="w-10 h-10 text-orange-500 mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">Voice-to-Text Speaking</h3>
            <p className="text-sm text-slate-600">
              Practica hablando y recibe transcripción instantánea
            </p>
          </div>
          <div className="bg-white p-6 border border-slate-200 animate-fade-in-up animation-delay-200">
            <PenLine className="w-10 h-10 text-green-500 mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">Feedback Ilimitado</h3>
            <p className="text-sm text-slate-600">
              Corrección de Writing con IA sin límites diarios
            </p>
          </div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {plans.map((plan, index) => (
            <div 
              key={plan.id}
              className={`relative bg-white p-8 border-2 transition-all animate-fade-in-up ${
                plan.popular 
                  ? 'border-amber-400 shadow-lg' 
                  : 'border-slate-200'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-900 text-xs font-bold px-4 py-1 rounded-full">
                  MÁS POPULAR
                </span>
              )}
              
              <h3 className="text-xl font-semibold text-slate-900 mb-2">{plan.name}</h3>
              
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                <span className="text-slate-500">{plan.period}</span>
              </div>
              
              {plan.savings && (
                <span className="inline-block bg-green-100 text-green-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
                  {plan.savings}
                </span>
              )}
              
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={processingPlan !== null || subscriptionStatus?.is_premium}
                className={`w-full rounded-full py-6 text-lg font-medium ${
                  plan.popular 
                    ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' 
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
                data-testid={`subscribe-${plan.id}-btn`}
              >
                {processingPlan === plan.id ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : subscriptionStatus?.is_premium ? (
                  'Ya eres Premium'
                ) : (
                  'Comenzar ahora'
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="bg-white p-8 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4">Preguntas frecuentes</h3>
          <div className="space-y-4">
            <div>
              <p className="font-medium text-slate-900">¿Puedo cancelar en cualquier momento?</p>
              <p className="text-sm text-slate-600">Sí, puedes cancelar tu suscripción cuando quieras sin penalización.</p>
            </div>
            <div>
              <p className="font-medium text-slate-900">¿Qué métodos de pago aceptan?</p>
              <p className="text-sm text-slate-600">Aceptamos tarjetas de crédito/débito Visa, Mastercard y American Express.</p>
            </div>
            <div>
              <p className="font-medium text-slate-900">¿Hay garantía de devolución?</p>
              <p className="text-sm text-slate-600">Sí, ofrecemos 7 días de garantía de devolución si no estás satisfecho.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
